import './first-ad-guide.css';

type FirstAdGuideProps = {
  mode: 'setup' | 'ready';
  brandName?: string;
  onAddBrand: () => void;
  onUsePrompt: (prompt: string) => void;
  onBrowse: () => void;
};

const SETUP_STEPS = [
  { number: '01', label: 'Add your website and product details.' },
  { number: '02', label: 'Create an ad from a clear brief.' },
  { number: '03', label: 'Refine it and export when it is ready.' },
];

export function FirstAdGuide({ mode, brandName, onAddBrand, onUsePrompt, onBrowse }: FirstAdGuideProps) {
  if (mode === 'setup') {
    return (
      <section className="first-ad-guide first-ad-guide--setup" data-testid="no-brand-gate" aria-labelledby="first-ad-guide-title">
        <span className="first-ad-guide__eyebrow">YOUR FIRST AD</span>
        <h2 id="first-ad-guide-title" className="first-ad-guide__title">Start with your brand.</h2>
        <p className="first-ad-guide__intro">
          Add your website and product details so Admoji can make ads that fit your brand.
        </p>
        <ol className="first-ad-guide__steps" aria-label="How it works">
          {SETUP_STEPS.map((step) => (
            <li className="first-ad-guide__step" key={step.number}>
              <span className="first-ad-guide__number" aria-hidden="true">{step.number}</span>
              <span>{step.label}</span>
            </li>
          ))}
        </ol>
        <div className="first-ad-guide__actions">
          <button className="first-ad-guide__primary" data-testid="no-brand-gate-add-brand" type="button" onClick={onAddBrand}>
            Add your brand <span aria-hidden="true">↗</span>
          </button>
          <button className="first-ad-guide__secondary" type="button" onClick={onBrowse}>
            Browse inspiration
          </button>
        </div>
      </section>
    );
  }

  const brand = brandName?.trim();
  const brandClause = brand ? ` for ${brand}` : '';
  const suggestions = [
    {
      title: 'Introduce a product',
      description: 'Start with what it is and who it helps.',
      prompt: `Help me create an ad introducing a product${brandClause}. Ask which product to feature and what customers should know about it before drafting.`,
    },
    {
      title: 'Lead with a benefit',
      description: 'Build the ad around one real advantage.',
      prompt: `Help me create a benefit-led ad${brandClause}. Ask which product and verified benefit to highlight before drafting.`,
    },
    {
      title: 'Shape my idea',
      description: 'Turn your own concept into a clear brief.',
      prompt: `I have an idea for an ad${brandClause}. Ask me to describe it, then help me turn it into a clear creative brief.`,
    },
  ];

  return (
    <details className="first-ad-guide first-ad-guide--ready">
      <summary className="first-ad-guide__summary">
        <span>Need a starting point?</span>
        <span className="first-ad-guide__summary-mark" aria-hidden="true">+</span>
      </summary>
      <div className="first-ad-guide__ready-content">
        <p className="first-ad-guide__ready-intro">
          Choose a brief to fill the prompt above. You can edit it before sending.
        </p>
        <div className="first-ad-guide__suggestions">
          {suggestions.map((suggestion) => (
            <button
              className="first-ad-guide__suggestion"
              key={suggestion.title}
              type="button"
              onClick={() => onUsePrompt(suggestion.prompt)}
            >
              <span className="first-ad-guide__suggestion-title">{suggestion.title}</span>
              <span className="first-ad-guide__suggestion-description">{suggestion.description}</span>
            </button>
          ))}
        </div>
        <p className="first-ad-guide__ready-footnote">
          Add a product or idea, make the ad, then refine and export it when you are happy with it.
        </p>
      </div>
    </details>
  );
}
